/**
 * DetailModal — Layer 3 template for detail/info views.
 * Fullscreen on mobile, centered card on desktop (same shell as FormModal).
 * Consumer provides all content.
 *
 * Consumers that are conditionally rendered (e.g. `{selected && <Modal>}`)
 * should manage their own closing state:
 *   const [isOpen, setIsOpen] = useState(true);
 *   <DetailModal isOpen={isOpen} onClose={() => setIsOpen(false)} onExitComplete={parentOnClose}>
 * This lets AnimatePresence play the exit animation before the parent unmounts.
 */

import { BaseModal } from './BaseModal';
import { MODAL_COLORS } from './constants';
import type { DetailModalProps } from './types';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';

export function DetailModal({
  isOpen,
  onClose,
  onExitComplete,
  preventClose,
  maxWidth,
  ariaLabel,
  flushTop = false,
  children,
}: DetailModalProps) {
  const isMobile = useMediaQuery(MD_BREAKPOINT);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      preventClose={preventClose}
      mobileVariant="fullscreen"
      maxWidth={maxWidth}
      ariaLabel={ariaLabel}
      onExitComplete={onExitComplete}
      flushTop={flushTop}
    >
      {/* Drag handle (mobile only, hidden when flushTop — caller renders its own) */}
      {isMobile && !flushTop && (
        <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
          <div
            className="w-12 h-1.5 rounded-full"
            style={{ backgroundColor: MODAL_COLORS.border, opacity: 0.3 }}
          />
        </div>
      )}
      {children}
    </BaseModal>
  );
}
